import { Target, Eye, Heart, Award, Users, Globe } from 'lucide-react';
import Layout from '../components/layout/Layout';

const AboutPage = () => {
  const team = [
    {
      name: 'Cheikh Abdoul Aziz COULIBALY',
      role: 'Co-fondateur, Juriste & Conseiller Marketing',
      description: 'Expert en droit des affaires avec une passion pour le marketing et l\'accompagnement juridique des entrepreneurs sénégalais.',
      initials: 'CA'
    },
    {
      name: 'Cheikh Talibouya Camara NDIAYE',
      role: 'Co-fondateur, Économiste & Conseiller Entrepreneurial',
      description: 'Spécialiste en économie, développement d\'entreprise et stratégie de financement.',
      initials: 'CT'
    }
  ];

  const values = [
    {
      icon: Target,
      title: 'Excellence',
      description: 'Nous visons l\'excellence dans chaque accompagnement pour garantir le succès de nos clients.'
    },
    {
      icon: Heart,
      title: 'Engagement',
      description: 'Nous nous engageons pleinement aux côtés de chaque entrepreneur que nous accompagnons.'
    },
    {
      icon: Users,
      title: 'Collaboration',
      description: 'Nous croyons en la force du travail d\'équipe et des partenariats durables.'
    },
    {
      icon: Globe,
      title: 'Impact',
      description: 'Nous œuvrons pour un impact positif sur l\'économie et la société sénégalaise.'
    }
  ];

  const stats = [
    { value: '57', label: 'Entreprises accompagnées' },
    { value: '2', label: 'Années d\'expertise' },
    { value: '17', label: 'Partenaires stratégiques' },
    { value: '95%', label: 'Taux de satisfaction' }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="py-20 md:py-32 gradient-hero" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">À propos de nous</span>
            <h1 className="hero-title mt-4" data-testid="about-title">
              Les architectes de votre <span className="text-[#006F33]">succès</span>
            </h1>
            <p className="hero-subtitle mt-6">
              Teranga Global Legal est bien plus qu'un cabinet de conseil juridique : nous sommes 
              les guides stratégiques qui vous accompagnent à chaque étape de votre aventure entrepreneuriale.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 md:py-32 bg-white" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            <div className="space-y-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#006F33]/10 flex items-center justify-center">
                  <Target className="w-7 h-7 text-[#006F33]" />
                </div>
                <h2 className="text-2xl font-bold text-[#1A1A1A]">Notre Mission</h2>
              </div>
              <p className="text-slate-600 leading-relaxed text-lg">
                Chez Teranga Global Legal, nous croyons que chaque projet mérite de voir le jour et de prospérer avec solidité. 
                Notre mission est d'accompagner les entrepreneurs sénégalais avec une expertise juridique de premier plan 
                et une méthodologie éprouvée : les 3F (Formation, Formalisation, Financement).
              </p>
              <p className="text-slate-600 leading-relaxed text-lg">
                Nous vous aidons à naviguer avec assurance dans le monde du droit des affaires, en vous apportant 
                des solutions juridiques adaptées à vos défis. Notre objectif ? Vous offrir une base légale solide 
                pour faire rayonner votre entreprise en toute sérénité.
              </p>
            </div>
            
            <div className="space-y-8">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-xl bg-[#E6C200]/20 flex items-center justify-center">
                  <Eye className="w-7 h-7 text-[#B39500]" />
                </div>
                <h2 className="text-2xl font-bold text-[#1A1A1A]">Notre Vision</h2>
              </div>
              <p className="text-slate-600 leading-relaxed text-lg">
                Avec Teranga Global Legal, vous ne rêvez pas d'un projet, vous le concrétisez. Nous sommes là pour 
                transformer vos ambitions en réalités et faire de votre entreprise un acteur majeur du paysage 
                économique du Sénégal.
              </p>
              <p className="text-slate-600 leading-relaxed text-lg">
                L'objectif n'est pas juste de créer des entreprises, mais de bâtir une économie forte en aidant 
                les entrepreneurs à prospérer et à créer des emplois durables. Parce que votre vision mérite 
                une stratégie gagnante.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-[#006F33]" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center" data-testid={`stat-${index}`}>
                <p className="text-4xl md:text-5xl font-bold text-white">{stat.value}</p>
                <p className="text-white/70 mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 md:py-32 bg-[#F9F9F7]" data-testid="team-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Notre Équipe</span>
            <h2 className="section-title mt-4">Les fondateurs</h2>
            <p className="section-subtitle mt-4 max-w-2xl mx-auto">
              Une équipe passionnée et expérimentée au service de votre réussite.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
            {team.map((member, index) => (
              <div key={index} className="team-card" data-testid={`team-member-${index}`}>
                <div className="h-64 bg-gradient-to-br from-[#006F33] to-[#004D24] flex items-center justify-center">
                  <span className="text-7xl font-bold text-white/30">{member.initials}</span>
                </div>
                <div className="p-8">
                  <h3 className="text-xl font-bold text-[#1A1A1A]">{member.name}</h3>
                  <p className="text-[#006F33] font-medium mt-1">{member.role}</p>
                  <p className="text-slate-600 mt-4 leading-relaxed">{member.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 md:py-32 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16">
            <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Nos Valeurs</span>
            <h2 className="section-title mt-4">Ce qui nous guide</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div key={index} className="text-center" data-testid={`value-${index}`}>
                <div className="w-16 h-16 rounded-2xl bg-[#006F33]/10 flex items-center justify-center mx-auto mb-6">
                  <value.icon className="w-8 h-8 text-[#006F33]" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-3">{value.title}</h3>
                <p className="text-slate-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* History Section */}
      <section className="py-20 md:py-32 gradient-brand" data-testid="history-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-sm font-semibold text-[#006F33] uppercase tracking-wider">Notre Histoire</span>
              <h2 className="section-title mt-4">Une passion née de l'expérience</h2>
              <div className="mt-8 space-y-6 text-slate-600 leading-relaxed">
                <p>
                  Teranga Global Legal est née de la conviction que chaque entrepreneur sénégalais mérite 
                  un accompagnement de qualité pour concrétiser ses ambitions. Fondée par Cheikh Abdoul Aziz 
                  COULIBALY et Cheikh Talibouya Camara NDIAYE, notre entreprise combine expertise juridique 
                  et conseil en entrepreneuriat.
                </p>
                <p>
                  Basés à Thiès, au cœur du Sénégal, nous avons développé la méthode des 3F pour offrir 
                  un parcours structuré et complet aux porteurs de projets : Formation pour acquérir 
                  les compétences, Formalisation pour sécuriser juridiquement, et Financement pour 
                  concrétiser la croissance.
                </p>
                <p>
                  Aujourd'hui, Teranga Global Legal est fière d'avoir accompagné 57 entreprises 
                  et de continuer à contribuer au développement économique du Sénégal.
                </p>
              </div>
            </div>
            
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1616970972068-4fe847e642da?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBkYWthciUyMHNlbmVnYWwlMjBhcmNoaXRlY3R1cmUlMjBhYnN0cmFjdHxlbnwwfHx8fDE3NzIxMzM4NDR8MA&ixlib=rb-4.1.0&q=85"
                alt="Architecture moderne au Sénégal"
                className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AboutPage;
